import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Table, Tag, Button, Spin, Typography, Space, Empty } from 'antd';
import { TrophyOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const { Title } = Typography;

const STATUS_COLOR = {
  draft:     'default',
  active:    'green',
  completed: 'blue',
  cancelled: 'red',
};

const OBJECT_LABEL = {
  Calls:        'Calls',
  Evaluations:  'Evaluations',
  Errors:       'Errors',
  Compliance:   'Compliance',
};

// Exported so the Campaign Summary's Engagements tab renders the same table
// rather than a second copy of these columns. It opens an engagement through
// onOpen when given one and navigates otherwise, because the Campaign Summary
// shows it in a panel instead of leaving the page.
export function EngagementTable({ rows, loading, navigate, onOpen }) {
  const columns = [
    {
      title: 'Name',
      dataIndex: 'Name',
      key: 'name',
      render: (v, r) => (
        <Space>
          <TrophyOutlined style={{ color: '#e8682a' }} />
          <span>{v}</span>
        </Space>
      ),
    },
    {
      title: 'Period',
      key: 'period',
      width: 150,
      render: (_, r) => `${r.StartMonth} – ${r.EndMonth}`,
    },
    {
      title: 'Object',
      dataIndex: 'Object',
      key: 'object',
      width: 110,
      render: (v) => OBJECT_LABEL[v] || v,
    },
    {
      title: 'Metric',
      dataIndex: 'Metric',
      key: 'metric',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'status',
      width: 100,
      render: (v) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_, r) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => onOpen
            ? onOpen(r.ID)
            : navigate(`/my-engagements/${r.ID}/leaderboard`)}
        >
          View
        </Button>
      ),
    },
  ];

  if (!loading && rows.length === 0) {
    return <Empty description="No engagements found" />;
  }

  return (
    <Table
      size="small"
      rowKey="ID"
      dataSource={rows}
      columns={columns}
      loading={loading}
      pagination={{ defaultPageSize: 25, hideOnSinglePage: true }}
    />
  );
}

export default function MyEngagementsPage() {
  const navigate = useNavigate();
  const [activeRows,  setActiveRows]  = useState([]);
  const [priorRows,   setPriorRows]   = useState([]);
  const [loadActive,  setLoadActive]  = useState(true);
  const [loadPrior,   setLoadPrior]   = useState(false);
  const [tabKey,      setTabKey]      = useState('active');

  const fetchActive = useCallback(() => {
    setLoadActive(true);
    api.get('/api/my-engagements', { params: { tab: 'active' } })
      .then((r) => setActiveRows(r.data))
      .catch(() => {})
      .finally(() => setLoadActive(false));
  }, []);

  const fetchPrior = useCallback(() => {
    setLoadPrior(true);
    api.get('/api/my-engagements', { params: { tab: 'prior' } })
      .then((r) => setPriorRows(r.data))
      .catch(() => {})
      .finally(() => setLoadPrior(false));
  }, []);

  useEffect(() => { fetchActive(); }, [fetchActive]);

  const handleTabChange = (key) => {
    setTabKey(key);
    if (key === 'prior' && priorRows.length === 0 && !loadPrior) {
      fetchPrior();
    }
  };

  const tabs = [
    {
      key: 'active',
      label: 'Active Engagements',
      children: (
        <EngagementTable rows={activeRows} loading={loadActive} navigate={navigate} />
      ),
    },
    {
      key: 'prior',
      label: 'Prior Engagements',
      children: (
        <EngagementTable rows={priorRows} loading={loadPrior} navigate={navigate} />
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>My Engagements & Contests</Title>
      <Tabs
        activeKey={tabKey}
        onChange={handleTabChange}
        items={tabs}
      />
    </div>
  );
}
